/**
 * SnapFind frontend analytics module.
 *
 * Captures two types of events and sends them to the WordPress REST API:
 *   - search  : fired when the user types a query and results render
 *   - click   : fired when the user clicks on a search result
 *
 * Both event types are sent together in the same request when the user
 * clicks a result, so the server always gets a matched search+click pair.
 * For searches with no click (browsing only), the search is sent after
 * an 800ms idle period followed by a 2-second flush delay.
 *
 * A UUID session ID is stored in sessionStorage and mirrored to a cookie
 * so the server-side WooCommerce integration can attribute purchases back
 * to the session that originated the search.
 */

/**
 * Return the session ID for this browser tab, creating one if needed.
 * The cookie copy is read server-side by WooCommerce at checkout time.
 */
const getSessionId = () => {
    let sessionId = sessionStorage.getItem('snapfind_session_id');
    if (!sessionId) {
        sessionId =
            typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
                      (
                          c ^
                          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
                      ).toString(16)
                  );
        sessionStorage.setItem('snapfind_session_id', sessionId);
        // 24-hour cookie so conversion attribution works across page navigations.
        document.cookie = `snapfind_session_id=${sessionId}; path=/; max-age=86400; SameSite=Lax`;
    }
    return sessionId;
};

/**
 * Detect the device type from the viewport width.
 * Used to segment analytics by device in the dashboard.
 */
const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
};

/**
 * Send an array of events to the REST ingestion endpoint.
 * Uses fetch with keepalive:true so the request completes even if the
 * user navigates away before the response arrives.
 * Falls back to sendBeacon for older browsers.
 *
 * @param {Array} events
 */
const trackEvent = (events) => {
    if (!events.length) return;

    const config = window.snapFindPublic;
    if (!config?.rest_url) return;

    const url = config.rest_url + 'snapfind/v1/analytics/event';
    const payload = JSON.stringify(events);

    try {
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
        }).catch(() => {
            // Swallow network errors — analytics must never break the UI.
        });
    } catch (e) {
        // Very old browser fallback.
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    }
};

/**
 * Factory that creates an InstantSearch middleware for a given post type.
 * One middleware instance is registered per search instance on the page.
 *
 * Search capture strategy:
 *   On click  — inject a search event for the current query into the same
 *               batch as the click (if it hasn't been logged yet), then flush.
 *   On render — start an 800ms idle timer; if the user doesn't click within
 *               that window, log the search and flush after 2 seconds.
 *
 * @param {string} postType  The post type this instance is searching.
 */
export const createAnalyticsMiddleware = (postType) => {
    let pendingEvents = [];
    let flushTimer = null;
    let queryDebounceTimer = null;
    let lastLoggedQuery = ''; // Tracks the last query we sent to avoid duplicates.

    const flush = () => {
        if (!pendingEvents.length) return;
        trackEvent(pendingEvents);
        pendingEvents = [];
    };

    return ({ instantSearchInstance }) => ({
        onStateChange({ uiState }) {},

        subscribe() {
            // Listen for clicks anywhere on the page and check if the target
            // is inside one of our search result wrappers.
            const handleHitClick = (e) => {
                const inner = e.target.closest('.snaf-hit-inner');
                if (!inner) return;

                const objectId = inner.dataset.snafId;
                const position = parseInt(inner.dataset.snafPos, 10);

                // Read query and result count directly from the live search instance.
                // This is the same source used to populate the click payload, so it's
                // always accurate regardless of debounce or render timing.
                const query = instantSearchInstance.helper?.state?.query || '';
                const nbHits = instantSearchInstance.results?.nbHits ?? 0;
                const hasResults = nbHits > 0 ? 1 : 0;

                if (!objectId) return;

                // Attach a search event to this batch if we haven't logged this
                // query yet. This ensures every click has a matching search record
                // even when the user clicks faster than the debounce fires.
                if (query && query.length > 1 && query !== lastLoggedQuery) {
                    clearTimeout(queryDebounceTimer);
                    clearTimeout(flushTimer);

                    // Guard: don't add a duplicate if the debounce already queued it.
                    const alreadyQueued = pendingEvents.some(
                        (ev) => ev.type === 'search' && ev.query === query
                    );
                    if (!alreadyQueued) {
                        pendingEvents.push({
                            type: 'search',
                            session_id: getSessionId(),
                            device: getDeviceType(),
                            query,
                            has_results: hasResults,
                            ts: Date.now(),
                        });
                        lastLoggedQuery = query;
                    }
                }

                // Always send the click event.
                pendingEvents.push({
                    type: 'click',
                    session_id: getSessionId(),
                    device: getDeviceType(),
                    query,
                    object_id: objectId,
                    position: isNaN(position) ? null : position,
                    ts: Date.now(),
                });

                flush();
            };

            document.addEventListener('click', handleHitClick);

            // Debounced search capture for browse-without-click behaviour.
            // Resets on every render so we only log once the query is stable.
            instantSearchInstance.on('render', () => {
                const query = instantSearchInstance.helper?.state?.query || '';
                const results = instantSearchInstance.results;

                // Skip: too short, no results object yet, or already logged.
                if (!query || query.length <= 1 || !results) return;
                if (query === lastLoggedQuery) return;

                clearTimeout(queryDebounceTimer);
                queryDebounceTimer = setTimeout(() => {
                    // A click might have already captured this query while we waited.
                    if (query === lastLoggedQuery) return;
                    if (pendingEvents.some((ev) => ev.type === 'search' && ev.query === query))
                        return;

                    pendingEvents.push({
                        type: 'search',
                        session_id: getSessionId(),
                        device: getDeviceType(),
                        query,
                        has_results: results.nbHits > 0 ? 1 : 0,
                        ts: Date.now(),
                    });
                    lastLoggedQuery = query;

                    // Give the user 2 more seconds to click before we send.
                    clearTimeout(flushTimer);
                    flushTimer = setTimeout(flush, 2000);
                }, 800);
            });

            // Send anything still in the queue when the page unloads.
            window.addEventListener('beforeunload', flush);
        },

        unsubscribe() {
            flush();
        },
    });
};

/**
 * Expose a simple function for manually tracking arbitrary events.
 * Can be called from theme code or other plugins.
 *
 * @param {string} type  Event type (e.g. 'impression').
 * @param {Object} data  Additional fields to merge into the event payload.
 */
export const trackManualEvent = (type, data = {}) => {
    trackEvent([
        {
            type,
            session_id: getSessionId(),
            device: getDeviceType(),
            ts: Date.now(),
            ...data,
        },
    ]);
};
